import { Children, isValidElement, type ReactElement, type ReactNode } from 'react';

import { SelectCell, SelectHeader } from '@/components/data-table/features/selection';
import type { SelectColumnProps } from '@/components/data-table/features/selection/select-column';

import type { FilterProps } from '../config/filter';
import type { GlobalFilterProps } from '../config/global-filter';
import type { ScrollToProps } from '../config/scroll-to';
import type { SortingProps } from '../config/sorting';
import type {
  CellProps,
  ColumnConfig,
  ColumnProps,
  FilterConfig,
  GlobalFilterConfig,
  HeaderProps,
  ParsedChildren,
  ScrollToConfig,
  SelectionConfig,
  SettingsConfig,
  SortingConfig,
  StylesConfig,
  StylesProps,
  TableDensity,
} from '../types';

// =============================================================================
// Constants
// =============================================================================

export const SELECT_COLUMN_ID = '_select';

// =============================================================================
// Default Settings
// =============================================================================

const DEFAULT_STYLES: StylesConfig = {
  row: {},
  cell: {},
  header: {},
};

const DEFAULT_SETTINGS: SettingsConfig = {
  density: 'default',
  stickyHeader: { enabled: false },
  cache: {},
  styles: DEFAULT_STYLES,
};

const DEFAULT_SELECTION: SelectionConfig = {
  enabled: false,
};

const DEFAULT_FILTER: FilterConfig = {};

const DEFAULT_GLOBAL_FILTER: GlobalFilterConfig = {};

const DEFAULT_SORTING_CONFIG: SortingConfig = {};

const DEFAULT_SCROLL_TO: ScrollToConfig = {};

// =============================================================================
// Element Type Guards
// =============================================================================

const hasDisplayName = (element: ReactElement, name: string): boolean =>
  typeof element.type === 'function' &&
  (element.type as { displayName?: string }).displayName === name;

const isColumnElement = (element: ReactElement): element is ReactElement<ColumnProps> =>
  hasDisplayName(element, 'DataTable.Column');

const isSelectColumnElement = (element: ReactElement): element is ReactElement<SelectColumnProps> =>
  hasDisplayName(element, 'DataTable.SelectColumn');

const isSettingsElement = (element: ReactElement): boolean =>
  hasDisplayName(element, 'DataTable.Settings');

const isBulkSelectBarElement = (element: ReactElement): boolean =>
  hasDisplayName(element, 'DataTable.BulkSelectBar');

const isHeaderElement = (element: ReactElement): element is ReactElement<HeaderProps> =>
  hasDisplayName(element, 'DataTable.Header');

const isCellElement = (element: ReactElement): element is ReactElement<CellProps> =>
  hasDisplayName(element, 'DataTable.Cell');

const isFilterElement = (element: ReactElement): element is ReactElement<FilterProps> =>
  hasDisplayName(element, 'DataTable.Filter');

const isGlobalFilterElement = (element: ReactElement): element is ReactElement<GlobalFilterProps> =>
  hasDisplayName(element, 'DataTable.GlobalFilter');

const isSortingElement = (element: ReactElement): element is ReactElement<SortingProps> =>
  hasDisplayName(element, 'DataTable.Sorting');

const isScrollToElement = (element: ReactElement): element is ReactElement<ScrollToProps> =>
  hasDisplayName(element, 'DataTable.ScrollTo');

const isColumnOverlayElement = (element: ReactElement): boolean =>
  hasDisplayName(element, 'DataTable.ColumnOverlay');

const isCellHighlightElement = (element: ReactElement): boolean =>
  hasDisplayName(element, 'DataTable.CellHighlight');

// =============================================================================
// Settings Parser
// =============================================================================

const parseSettings = (children: ReactNode): SettingsConfig => {
  const settings: SettingsConfig = { ...DEFAULT_SETTINGS };

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;

    const displayName = (child.type as { displayName?: string }).displayName;

    switch (displayName) {
      case 'DataTable.Density':
        settings.density = (child.props as { value: TableDensity }).value;
        break;
      case 'DataTable.StickyHeader':
        settings.stickyHeader = {
          enabled: true,
          height: (child.props as { height?: string }).height,
        };
        break;
      case 'DataTable.Cache': {
        const properties = (child.props as { properties: string[] }).properties;
        settings.cache = {
          sizing: properties.includes('sizing'),
          sorting: properties.includes('sorting'),
        };
        break;
      }
      case 'DataTable.Virtualize':
        settings.virtualize = {
          enabled: true,
          overscan: (child.props as { overscan?: number; estimateSize?: number }).overscan,
          estimateSize: (child.props as { overscan?: number; estimateSize?: number }).estimateSize,
        };
        break;
      case 'DataTable.DragScroll':
        settings.dragScroll = true;
        break;
      case 'DataTable.Styles': {
        const stylesProps = child.props as StylesProps;
        settings.styles = {
          row: stylesProps.row ?? {},
          cell: stylesProps.cell ?? {},
          header: stylesProps.header ?? {},
        };
        break;
      }
    }
  });

  return settings;
};

// =============================================================================
// Column Parser
// =============================================================================

const parseColumn = <TData>(element: ReactElement<ColumnProps<TData>>): ColumnConfig<TData> => {
  const props = element.props;

  // Extract Header and Cell from children
  let headerContent: ReactNode = null;
  let cellRenderer: ColumnConfig<TData>['cellRenderer'] = undefined;
  let cellType: ColumnConfig<TData>['cellType'] = undefined;
  let cellHoverClassName: ColumnConfig<TData>['cellHoverClassName'] = undefined;

  Children.forEach(props.children, (child) => {
    if (!isValidElement(child)) return;

    if (isHeaderElement(child)) {
      headerContent = child.props.children;
    } else if (isCellElement(child)) {
      const cellProps = child.props as CellProps<TData>;
      cellHoverClassName = cellProps.hoverClassName;
      if (cellProps.type) {
        cellType = cellProps.type;
      } else if (cellProps.children) {
        cellRenderer = cellProps.children;
      }
    }
  });

  return {
    id: props.id,
    accessorKey: props.accessor as string | undefined,
    accessorFn: props.accessorFn,

    // Header
    headerContent,

    // Cell
    cellRenderer,
    cellType,
    cellHoverClassName,

    // Sizing
    size: props.width,
    minSize: props.minWidth,
    maxSize: props.maxWidth,
    enableResizing: props.resizable ?? true,
    growFirst: props.growFirst,

    // Sorting
    enableSorting: props.sortable ?? true,
    sortDescFirst: props.sortDescFirst,
    sortingFn: props.sortingFn,

    // Filtering - enable if filterType OR filterFn is specified
    enableFiltering: props.filterType !== undefined || props.filterFn !== undefined,
    filterType: props.filterType,
    filterFn: props.filterFn,
    filterOptions: props.filterOptions,
    filterPlaceholder: props.filterPlaceholder,

    // Visibility
    hidden: props.hidden,

    // Pinning
    pinned: props.pinned,

    // Mobile
    mobile: props.mobile,
  };
};

// =============================================================================
// Selection Column Parser
// =============================================================================

const parseSelectColumn = <TData>(
  element: ReactElement<SelectColumnProps<TData>>
): SelectionConfig<TData> => {
  const props = element.props;

  return {
    enabled: true,
    // Controlled/uncontrolled mode props
    value: props.value,
    initial: props.initial,
    onChange: props.onChange,
    // Feature flags
    enableMultiSelect: props.enableMultiSelect ?? true,
    selectAllMode: props.selectAllMode ?? 'all',
    // Per-row selection control
    enableRowSelection: props.enableRowSelection,
    disabledTooltip: props.disabledTooltip,
    selectAllDisabledTooltip: props.selectAllDisabledTooltip,
    // Sorting
    sortable: props.sortable ?? false,
    sortingFn: props.sortingFn,
  };
};

// =============================================================================
// Main Parser
// =============================================================================

/**
 * Creates a column config for the SelectColumn (checkbox column).
 */
const createSelectColumnConfig = <TData>(
  selectionConfig: SelectionConfig<TData>
): ColumnConfig<TData> => {
  return {
    id: SELECT_COLUMN_ID,
    headerContent: null,
    // Custom header renderer that renders the SelectHeader component. Tooltips are
    // read from the table meta: this config is cached, the meta is current.
    customHeaderRenderer: (table) =>
      SelectHeader({
        table,
        selectAllMode: selectionConfig.selectAllMode,
        disabledTooltip: table.options.meta?.selectAllDisabledTooltip,
      }),
    // Custom cell renderer that renders the SelectCell component
    cellRenderer: ({ row, table }) =>
      SelectCell({ row, disabledTooltip: table.options.meta?.selectDisabledTooltip }),
    // Fixed sizing - checkbox column doesn't need to be resizable
    size: 40,
    minSize: 40,
    maxSize: 40,
    enableResizing: false,
    // Sorting: consumer provides sortingFn, just like a regular column
    // Default to sorting by selection state before unselected
    enableSorting: selectionConfig.sortable ?? false,
    ...(selectionConfig.sortable && {
      accessorFn: () => 0, // Dummy accessor to satisfy TanStack's getCanSort()
      sortingFn:
        selectionConfig.sortingFn ??
        ((rowA, rowB) => {
          const a = rowA.getIsSelected() ? 0 : 1;
          const b = rowB.getIsSelected() ? 0 : 1;
          return a - b;
        }),
    }),
    enableFiltering: false,
    // Pin to left edge
    pinned: 'left',
  };
};

/**
 * Parses DataTable children to extract column configs, settings, and selection.
 *
 * This is called during render (not in an effect) to avoid the stale children
 * problem that caused re-registration on every parent render.
 *
 * @param children - ReactNode children from DataTable
 * @returns Parsed configuration for columns, settings, and selection
 */
export const parseChildren = <TData>(children: ReactNode): ParsedChildren<TData> => {
  const columns: ColumnConfig<TData>[] = [];
  let settings: SettingsConfig = { ...DEFAULT_SETTINGS };
  let selection: SelectionConfig<TData> = { ...DEFAULT_SELECTION } as SelectionConfig<TData>;
  let bulkSelectBar: ReactElement | null = null;
  let columnOverlay: ReactElement | null = null;
  let cellHighlight: ReactElement | null = null;
  let filter: FilterConfig = { ...DEFAULT_FILTER };
  let globalFilter: GlobalFilterConfig<TData> = {
    ...DEFAULT_GLOBAL_FILTER,
  } as GlobalFilterConfig<TData>;
  let sortingConfig: SortingConfig = { ...DEFAULT_SORTING_CONFIG };
  let scrollTo: ScrollToConfig = { ...DEFAULT_SCROLL_TO };

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;

    if (isColumnElement(child)) {
      columns.push(parseColumn(child as ReactElement<ColumnProps<TData>>));
    } else if (isSelectColumnElement(child)) {
      selection = parseSelectColumn(child as ReactElement<SelectColumnProps<TData>>);
    } else if (isSettingsElement(child)) {
      settings = parseSettings((child.props as { children?: ReactNode }).children);
    } else if (isBulkSelectBarElement(child)) {
      // Pass through the BulkSelectBar element as-is (it's a rendering component)
      bulkSelectBar = child;
    } else if (isColumnOverlayElement(child)) {
      // Pass through the ColumnOverlay element as-is (it's a rendering component)
      columnOverlay = child;
    } else if (isCellHighlightElement(child)) {
      // Pass through the CellHighlight element as-is (it's a rendering component)
      cellHighlight = child;
    } else if (isFilterElement(child)) {
      filter = {
        value: child.props.value,
        initial: child.props.initial,
        onChange: child.props.onChange,
      };
    } else if (isGlobalFilterElement(child)) {
      const props = child.props as GlobalFilterProps<TData>;
      globalFilter = {
        value: props.value,
        initial: props.initial,
        onChange: props.onChange,
        filterFn: props.filterFn,
      };
    } else if (isSortingElement(child)) {
      sortingConfig = {
        value: child.props.value,
        initial: child.props.initial,
        onChange: child.props.onChange,
      };
    } else if (isScrollToElement(child)) {
      scrollTo = {
        columnId: child.props.columnId,
        align: child.props.align,
        offset: child.props.offset,
      };
    }
  });

  // If selection is enabled, add the select column at the beginning
  if (selection.enabled) {
    columns.unshift(createSelectColumnConfig(selection));
  }

  return {
    columns,
    settings,
    selection,
    bulkSelectBar,
    columnOverlay,
    cellHighlight,
    filter,
    globalFilter,
    sorting: sortingConfig,
    scrollTo,
  };
};

// =============================================================================
// Structural Equality Helper
// =============================================================================

/**
 * Serializes column configs for structural equality comparison.
 * Used to avoid unnecessary re-renders when children reference changes
 * but actual configuration hasn't actually changed.
 */
export const serializeColumns = <TData>(columns: ColumnConfig<TData>[]): string => {
  return JSON.stringify(
    columns.map((col) => ({
      id: col.id,
      accessorKey: col.accessorKey,
      size: col.size,
      minSize: col.minSize,
      maxSize: col.maxSize,
      enableResizing: col.enableResizing,
      enableSorting: col.enableSorting,
      sortDescFirst: col.sortDescFirst,
      enableFiltering: col.enableFiltering,
      filterType: col.filterType,
      pinned: col.pinned,
      hidden: col.hidden,
      cellType: col.cellType,
      // Note: We don't serialize functions (accessorFn, cellRenderer, etc.)
      // as they would always differ. The assumption is if the serialized
      // config is the same, the functions are likely the same.
    }))
  );
};

/**
 * Serializes settings config for structural equality comparison.
 * Used to detect changes in settings (especially styles) that should trigger
 * a re-parse of children even if columns haven't changed.
 */
export const serializeSettings = (settings: SettingsConfig): string => {
  return JSON.stringify({
    density: settings.density,
    stickyHeader: settings.stickyHeader,
    cache: settings.cache,
    virtualize: settings.virtualize,
    dragScroll: settings.dragScroll,
    styles: settings.styles,
  });
};
